import React, { useState, useEffect } from "react"
import Header from "../../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Paper, Typography, Grid, Card, Stack, Snackbar, Tooltip, Button } from "@mui/material"
import { Home, Person, Assignment, CalendarToday, Gavel, Percent, Visibility } from "@mui/icons-material"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import CustomBackdrop from "../../../components/CustomBackdrop"
import Swal from "sweetalert2"
import CustomTextFieldReadable from "../../../components/CustomTextFieldReadable"
import CustomAutocomplete from "../../../components/CustomAutocomplete"
import { useQuery, api } from "../../../api"
import CustomTextField from "../../../components/CustomTextField"

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const ESTADOS = [
  { value: "ACTIVO", label: "ACTIVO" },
  { value: "INACTIVO", label: "INACTIVO" },
]

const TIPO_DOCUMENTO_OPTIONS = [
  { value: "C", label: "Cédula" },
  { value: "R", label: "RUC" },
  { value: "P", label: "Pasaporte" },
]

const BuscarDetalleATitularidad = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const titularidadData = location.state

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [error, setError] = useState(null)

  // Obtener IDs de la titularidad desde el state
  const titid = titularidadData?.titid
  const preid = titularidadData?.preid

  // API para obtener los títulos de adquisición
  const { data: titulosData = {} } = useQuery({
    queryKey: ["getTitulosAdquisicionCB"],
    url: "/ActivacionDePredios/getTitulosAdquisicionCB",
  })

  // API para obtener los predios
  const { data: prediosData = {} } = useQuery({
    queryKey: ["getPrediosCB"],
    url: "/ActivacionDePredios/getPrediosCB",
  })

  // API para obtener datos específicos de la titularidad
  const {
    data: { data: titularidadDetalleData } = {},
    isLoading: isLoadingTitularidad,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: ["getSpecificTitularidad", titid, preid],
    url: "/ActivacionDePredios/getSpecificTitularidad",
    params: { titid, preid },
    enabled: !!(titid && preid),
    refetchOnMount: true,
    staleTime: 0,
  })

  // Estado para el formulario (solo visualización)
  const [formData, setFormData] = useState({
    titid: "",
    preid: null,
    titadqpre: null,
    titfecha: "",
    tittipodocprop: null,
    titidpropietario: "",
    titnombresprop: "",
    titporcenacc: "",
    titnotaria: "",
    titestado: null,
  })

  // Validar que llegaron datos
  useEffect(() => {
    if (!titularidadData) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se encontró información de la titularidad",
      }).then(() => {
        navigate(-1)
      })
    }
  }, [titularidadData, navigate])

  // Manejar errores de la query
  useEffect(() => {
    if (isError) {
      const errorMsg = queryError?.response?.data?.message || "Error al cargar los datos de la titularidad"
      setError(errorMsg)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMsg,
      })
    }
  }, [isError, queryError])

  // Cargar datos cuando se recibe la respuesta
  useEffect(() => {
    if (titularidadDetalleData) {
      const titularidad = titularidadDetalleData

      // Formatear fecha para input type="date" (YYYY-MM-DD)
      let fechaFormateada = ""
      if (titularidad.titfecha) {
        const fecha = new Date(titularidad.titfecha)
        fechaFormateada = fecha.toISOString().split("T")[0]
      }

      // Buscar el objeto completo para los comboboxes (para mantener el mismo formato)
      const predioEncontrado = prediosData?.data?.find((p) => p.value === titularidad.preid) || {
        value: titularidad.preid,
        label: titularidad.preid,
      }

      const tituloEncontrado = titulosData?.data?.find((t) => t.value === titularidad.titadqpre) || {
        value: titularidad.titadqpre,
        label: titularidad.titadqpre,
      }

      const tipoDocEncontrado = TIPO_DOCUMENTO_OPTIONS.find((t) => t.value === titularidad.tittipodocprop) || {
        value: titularidad.tittipodocprop,
        label: titularidad.tittipodocprop,
      }

      const estadoEncontrado = ESTADOS.find((e) => e.value === titularidad.titestado) || {
        value: titularidad.titestado,
        label: titularidad.titestado,
      }

      setFormData({
        titid: titularidad.titid || "",
        preid: predioEncontrado,
        titadqpre: tituloEncontrado,
        titfecha: fechaFormateada,
        tittipodocprop: tipoDocEncontrado,
        titidpropietario: titularidad.titidpropietario || "",
        titnombresprop: titularidad.titnombresprop || "",
        titporcenacc: titularidad.titporcenacc?.toString() || "",
        titnotaria: titularidad.titnotaria || "",
        titestado: estadoEncontrado,
      })
    }
  }, [titularidadDetalleData, prediosData, titulosData])

  // Extraer datos de las APIs
  const titulosList = titulosData?.data || []
  const prediosList = prediosData?.data || []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Consulta de Titularidad de Predio</b>
        </div>

        <CustomBackdrop isLoading={isLoadingTitularidad} />

        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error" align="center">
              {error}
            </Typography>
          </Box>
        )}

        <Box sx={StyledRoot}>
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3, background: "white" }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ color: "primary.main", mb: 3, display: "flex", alignItems: "center", gap: 1 }}
            >
              <Assignment /> Información de Titularidad
            </Typography>

            <Grid container spacing={3}>
              {/* Fila 1: ID y Estado */}
              <Grid item xs={12} md={3}>
                <CustomTextFieldReadable disabled label="ID Inscripción" value={formData.titid} />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomAutocomplete
                  label="Estado"
                  selectedOption={formData.titestado}
                  setSelectedOption={() => {}} // Vacío porque es solo lectura
                  options={ESTADOS}
                  disabled
                />
              </Grid>

              {/* Fila 2: Comboboxes principales */}
              <Grid item xs={12} md={6}>
                <CustomAutocomplete
                  label="Predio"
                  selectedOption={formData.preid}
                  setSelectedOption={() => {}} // Vacío porque es solo lectura
                  options={prediosList}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomAutocomplete
                  label="Título de Adquisición"
                  selectedOption={formData.titadqpre}
                  setSelectedOption={() => {}} // Vacío porque es solo lectura
                  options={titulosList}
                  disabled
                />
              </Grid>

              {/* Fila 3: Fecha y Notaría */}
              <Grid item xs={12} md={6}>
                <CustomTextField
                  fullWidth
                  label="Fecha de Titularidad"
                  type="date"
                  value={formData.titfecha}
                  onChange={() => {}} // Vacío porque es solo lectura
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomTextField
                  fullWidth
                  label="Notaría"
                  value={formData.titnotaria}
                  onChange={() => {}} // Vacío porque es solo lectura
                  disabled
                  placeholder="Ej: Notaría 24 del Cantón"
                />
              </Grid>

              {/* Sección de Propietario */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ mt: 2, mb: 2, color: "primary.main", display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Person /> Información del Propietario
                </Typography>
              </Grid>

              {/* Fila 4: Tipo Documento, Número y Porcentaje */}
              <Grid item xs={12} md={4}>
                <CustomAutocomplete
                  label="Tipo de Documento"
                  selectedOption={formData.tittipodocprop}
                  setSelectedOption={() => {}} // Vacío porque es solo lectura
                  options={TIPO_DOCUMENTO_OPTIONS}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomTextField
                  fullWidth
                  label="Número de Documento"
                  value={formData.titidpropietario}
                  onChange={() => {}} // Vacío porque es solo lectura
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomTextField
                  fullWidth
                  label="Porcentaje de Acciones"
                  type="number"
                  value={formData.titporcenacc}
                  onChange={() => {}} // Vacío porque es solo lectura
                  disabled
                  inputProps={{ step: "0.01" }}
                />
              </Grid>

              {/* Fila 5: Nombres completos */}
              <Grid item xs={12}>
                <CustomTextField
                  fullWidth
                  label="Apellidos y Nombres del Propietario"
                  value={formData.titnombresprop}
                  onChange={() => {}} // Vacío porque es solo lectura
                  disabled
                  placeholder="Ej: Carlos Andrade y María Solís"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </div>
    </ThemeProvider>
  )
}

export default BuscarDetalleATitularidad
